import {
  Box,
  useTheme,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { useStateContext } from "contexts/ContextProvider";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import Grid from "@mui/material/Grid2";

import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import api from "services/api";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import Overlay from "components/Overlay/Overlay";

const RequestorGroupNewEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const { overlayData, setOverlayName, triggerPageRefresh, showSnackbar } =
    useStateContext();
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);

  const { currentUser } = useAuth();
  const { socket } = useWebSocket();

  const isNewGroup =
    overlayData.Action === OVERLAY_NAMES.REQUESTOR_GROUP_ACTION_NEW;

  // Edit lock for existing groups only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "requestorgroup",
    isNewGroup ? null : overlayData.Group?.id,
  );

  const requestors = Array.isArray(overlayData.Requestors)
    ? overlayData.Requestors
    : [];

  // Requestors already used by ANOTHER group (as Member or CC) are not
  // available for picking here. The currently-edited group's own members/CCs
  // stay selectable.
  const currentGroupId = isNewGroup
    ? null
    : Number(overlayData.Group?.id || null);

  const unavailableRequestorIds = useMemo(() => {
    const ids = new Set();
    const groups = Array.isArray(overlayData.Groups)
      ? overlayData.Groups
      : [];
    groups.forEach((group) => {
      if (Number(group.id) === currentGroupId) return;
      (group.Members || []).forEach((member) =>
        ids.add(Number(member.id)),
      );
      (group.CCRequestors || []).forEach((cc) => ids.add(Number(cc.id)));
    });
    return ids;
  }, [currentGroupId, overlayData.Groups]);

  const [name, setName] = useState(
    isNewGroup ? "" : overlayData.Group?.Name || "",
  );
  const [primaryInvoicerId, setPrimaryInvoicerId] = useState(
    isNewGroup ? null : overlayData.Group?.PrimaryInvoicer?.id || null,
  );
  const [memberRequestorIds, setMemberRequestorIds] = useState(
    isNewGroup
      ? []
      : (overlayData.Group?.Members || []).map((m) => Number(m.id)),
  );
  const [ccRequestorIds, setCcRequestorIds] = useState(
    isNewGroup
      ? []
      : (overlayData.Group?.CCRequestors || []).map((cc) => Number(cc.id)),
  );

  const [nameError, setNameError] = useState(false);
  const [shouldValidate, setShouldValidate] = useState(false);

  const validateFields = useCallback(() => {
    setNameError(!name || name.trim() === "");
  }, [name]);

  const addNewGroup = useCallback(() => {
    setIsSavingChanges(true);
    setIsEditable(false);

    api
      .post("/requestorGroups", {
        ClientID: overlayData.ClientID,
        Name: name.trim(),
        PrimaryInvoicerID: primaryInvoicerId,
        MemberRequestorIDs: memberRequestorIds,
        CCRequestorIDs: ccRequestorIds,
      })
      .then((response) => {
        setIsSavingChanges(false);

        const clientId = overlayData?.ClientID;
        const changeAction = "Created";
        if (socket && clientId) {
          socket.emit("entity:saved", {
            modelType: "Client",
            recordId: clientId,
            changeType: "RequestorGroup",
            changeAction,
            user: {
              id: currentUser?.id,
              name: currentUser?.Name || "User",
              imageThumbnail: currentUser?.ImageThumbnail,
            },
          });
        }

        triggerPageRefresh("New Group Added Successfully!", theme);

        setOverlayName("");
      })
      .catch((err) => {
        console.error("Error creating requestor group:", err);
        setIsSavingChanges(false);
        setIsEditable(true);
        showSnackbar(
          err?.response?.data?.error ||
            "Failed to create requestor group",
          theme,
          "error",
        );
      });
  }, [
    ccRequestorIds,
    currentUser?.ImageThumbnail,
    currentUser?.Name,
    currentUser?.id,
    memberRequestorIds,
    name,
    overlayData?.ClientID,
    primaryInvoicerId,
    setOverlayName,
    showSnackbar,
    socket,
    theme,
    triggerPageRefresh,
  ]);

  const updateGroup = useCallback(() => {
    setIsSavingChanges(true);
    setIsEditable(false);

    api
      .put(`/requestorGroups/${overlayData.Group.id}`, {
        Name: name.trim(),
        PrimaryInvoicerID: primaryInvoicerId,
        MemberRequestorIDs: memberRequestorIds,
        CCRequestorIDs: ccRequestorIds,
      })
      .then((response) => {
        setIsSavingChanges(false);

        const clientId = overlayData?.ClientID;
        const changeAction = "Updated";
        if (socket && clientId) {
          socket.emit("entity:saved", {
            modelType: "Client",
            recordId: clientId,
            changeType: "RequestorGroup",
            changeAction,
            user: {
              id: currentUser?.id,
              name: currentUser?.Name || "User",
              imageThumbnail: currentUser?.ImageThumbnail,
            },
          });
        }

        triggerPageRefresh("Group Updated Successfully", theme);

        setOverlayName("");
      })
      .catch((err) => {
        console.error("Error updating requestor group:", err);
        setIsSavingChanges(false);
        setIsEditable(true);
        showSnackbar(
          err?.response?.data?.error ||
            "Failed to update requestor group",
          theme,
          "error",
        );
      });
  }, [
    ccRequestorIds,
    currentUser?.ImageThumbnail,
    currentUser?.Name,
    currentUser?.id,
    memberRequestorIds,
    name,
    overlayData?.ClientID,
    overlayData?.Group?.id,
    primaryInvoicerId,
    setOverlayName,
    showSnackbar,
    socket,
    theme,
    triggerPageRefresh,
  ]);

  useEffect(() => {
    if (shouldValidate) {
      if (!nameError) {
        isNewGroup ? addNewGroup() : updateGroup();
      }
      setShouldValidate(false);
    }
  }, [
    addNewGroup,
    isNewGroup,
    nameError,
    shouldValidate,
    updateGroup,
  ]);

  const saveGroup = useCallback(() => {
    validateFields();
    setShouldValidate(true);
  }, [validateFields]);

  const attemptSave = useCallback(() => {
    if (isSavingChanges || isLocked) {
      return;
    }

    // Block saving while Members and Primary Invoicer are both empty.
    if (memberRequestorIds.length === 0 && primaryInvoicerId === null) {
      return;
    }

    saveGroup();
  }, [
    isLocked,
    isSavingChanges,
    memberRequestorIds.length,
    primaryInvoicerId,
    saveGroup,
  ]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      const tagName = target?.tagName;
      const isTextArea = tagName === "TEXTAREA";
      const isContentEditable = !!target?.isContentEditable;
      const isInsideDialog =
        target instanceof Element && !!target.closest('[role="dialog"]');

      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        !isTextArea &&
        !isContentEditable &&
        !isInsideDialog
      ) {
        e.preventDefault();
        attemptSave();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [attemptSave]);

  const primaryInvoicerValue = requestors.find(
    (r) => Number(r.id) === Number(primaryInvoicerId),
  ) || null;

  const memberValues = requestors.filter((r) =>
    memberRequestorIds.includes(Number(r.id)),
  );

  const ccValues = requestors.filter((r) =>
    ccRequestorIds.includes(Number(r.id)),
  );

  // A group must have at least one Member or a Primary Invoicer before it
  // can be saved — the Save button stays greyed out otherwise.
  const hasMembersOrInvoicer =
    memberRequestorIds.length > 0 || primaryInvoicerId !== null;

  const footer = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "end",
        alignItems: "center",
        width: "100%",
        flexDirection: "row",
        p: ".75rem 2rem",
      }}
    >
      {isMobile ? (
        <Tooltip title="Discard" placement="top">
          <IconButton
            color="primary"
            onClick={() => setOverlayName("")}
            sx={{
              width: "40px",
              height: "40px",
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: "12px",
            }}
          >
            <DisabledByDefaultRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          color="primary"
          variant="outlined"
          size="large"
          endIcon={<DisabledByDefaultRoundedIcon />}
          onClick={() => setOverlayName("")}
          sx={{
            borderRadius: "12px",
          }}
        >
          Discard
        </Button>
      )}
      {isMobile ? (
        <Tooltip
          title={
            hasMembersOrInvoicer
              ? "Save"
              : "Add at least one Member or a Primary Invoicer"
          }
          placement="top"
        >
          <IconButton
            color="primary"
            onClick={attemptSave}
            disabled={isSavingChanges || isLocked || !hasMembersOrInvoicer}
            sx={{
              width: "40px",
              height: "40px",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.background.default,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            <SaveRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          loading={isSavingChanges}
          disabled={isSavingChanges || isLocked || !hasMembersOrInvoicer}
          color="primary"
          variant="contained"
          size="large"
          endIcon={<SaveRoundedIcon />}
          type="submit"
          onClick={attemptSave}
          sx={{
            borderRadius: "12px",
          }}
        >
          Save
        </Button>
      )}
    </Box>
  );

  return (
    <>
      <Overlay
        id="requestor-group-new-edit"
        open={true}
        onClose={() => setOverlayName("")}
        title={isNewGroup ? "New Group" : "Edit Group"}
        height="fit-content"
        footer={footer}
      >
        <Box
          component="form"
          sx={{
            display: "flex",
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            overflowY: "auto",
            width: "100%",
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <Grid container spacing={2} width="100%">
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                disabled={!isEditable}
                size="small"
                id={"Group_Name"}
                label="Group Name"
                error={nameError}
                helperText={nameError ? "Please enter a group name" : ""}
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setNameError(
                    !event.target.value || event.target.value.trim() === "",
                  );
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                disabled={!isEditable}
                size="small"
                options={requestors}
                getOptionLabel={(requestor) => requestor?.Name || ""}
                getOptionKey={(requestor) =>
                  `${requestor?.id ?? requestor?.Name}-${requestors.indexOf(requestor)}`
                }
                value={primaryInvoicerValue}
                onChange={(event, newValue) =>
                  setPrimaryInvoicerId(newValue ? Number(newValue.id) : null)
                }
                renderInput={(params) => (
                  <TextField {...params} label="Primary Invoicer" size="small" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                disabled={!isEditable}
                multiple
                size="small"
                options={requestors}
                getOptionLabel={(requestor) => requestor?.Name || ""}
                getOptionKey={(requestor) =>
                  `${requestor?.id ?? requestor?.Name}-${requestors.indexOf(requestor)}`
                }
                getOptionDisabled={(requestor) =>
                  unavailableRequestorIds.has(Number(requestor?.id))
                }
                value={memberValues}
                onChange={(event, newValue) =>
                  setMemberRequestorIds(newValue.map((r) => Number(r.id)))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Members" size="small" />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                disabled={!isEditable}
                multiple
                size="small"
                options={requestors}
                getOptionLabel={(requestor) => requestor?.Name || ""}
                getOptionKey={(requestor) =>
                  `${requestor?.id ?? requestor?.Name}-${requestors.indexOf(requestor)}`
                }
                getOptionDisabled={(requestor) =>
                  unavailableRequestorIds.has(Number(requestor?.id))
                }
                value={ccValues}
                onChange={(event, newValue) =>
                  setCcRequestorIds(newValue.map((r) => Number(r.id)))
                }
                renderInput={(params) => (
                  <TextField {...params} label="CC Requestors" size="small" />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </Overlay>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </>
  );
};

export default RequestorGroupNewEdit;
